import { useState, useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { ChevronDown } from "lucide-react";
import SEO, { faqSchema } from "@/components/SEO";

const FAQS = [
  {
    question: "What is NeuroNest?",
    answer:
      "NeuroNest is an app designed for neurodivergent and SEND (Special Educational Needs and Disabilities) individuals and their families. It helps you find sensory-friendly places, manage daily routines, use communication tools, and access calming fidget activities — all in one place.",
  },
  {
    question: "Who is NeuroNest for?",
    answer:
      "NeuroNest is for neurodivergent individuals, parents, carers, and children. The app offers four distinct modes — Child, Parent, Carer, and Individual — each tailored with age-appropriate features and relevant controls.",
  },
  {
    question: "What are the different app modes?",
    answer:
      "Child Mode — A simplified, sensory-friendly interface with routines, fidget tools, and communication cards.\n\nParent Mode — Manage child profiles, create routines, receive check-in notifications, and view insights.\n\nCarer Mode — Similar to Parent mode, designed for professional carers supporting SEND individuals.\n\nIndividual Mode — Full access to places, routines, check-ins, and personal insights for independent users.",
  },
  {
    question: "How many SEND-friendly places are listed?",
    answer:
      "NeuroNest features over 1,370 verified SEND-friendly locations across the UK, including soft play centres, sensory rooms, swimming pools, cinemas, museums, libraries, cafes, hair salons, and more.",
  },
  {
    question: "What information is available for each place?",
    answer:
      "Each place includes sensory tags (e.g. lighting level, noise level), quiet times, typical crowd levels, photos, directions, address, and category. Many also include SEND-specific descriptions highlighting accessibility features.",
  },
  {
    question: "What categories of places can I browse?",
    answer:
      "Categories include soft play, trampoline parks, sensory rooms, swimming, outdoor activities, cinemas, museums, libraries, cafes, climbing, skating, skiing, retail, nature reserves, community centres, theatres, culture venues, entertainment, food outlets, and haircuts.",
  },
  {
    question: "How do check-ins work?",
    answer:
      "Check-ins allow users to log how they're feeling — tracking mood, emotions, and sensory experiences. Parents and carers receive notifications when their child completes a check-in, helping them understand wellbeing patterns over time.",
  },
  {
    question: "What are routines and how do I use them?",
    answer:
      "Routines are step-by-step visual schedules you can create for daily tasks (e.g. morning routine, bedtime routine). Parents can create routines for their children, and the Routine Player guides the child through each step with progress tracking.",
  },
  {
    question: "What are communication cards?",
    answer:
      "Communication cards are visual aids that help non-verbal or minimally verbal individuals express their needs, feelings, and choices. They can be customised to suit each user.",
  },
  {
    question: "What fidget tools are available?",
    answer:
      "The app includes digital fidget tools like a spinner, designed to provide calming sensory input. These are available in Child mode and can be used during moments of anxiety or overstimulation.",
  },
  {
    question: "How do push notifications work?",
    answer:
      "NeuroNest sends push notifications for important events — such as when a child completes a check-in, finishes a routine, or when a parent creates a new routine for them. Notifications are filtered by app mode so you only receive what's relevant to you.",
  },
  {
    question: "Can I report incorrect place information?",
    answer:
      'Yes. On any place detail page, tap "Report Issue" to flag incorrect data such as wrong address, outdated opening times, or inaccurate sensory information. Our team reviews every report.',
  },
  {
    question: "Is my data secure?",
    answer:
      "Yes. NeuroNest uses secure authentication and encrypted data storage with row-level security policies. Your personal data and your children's information are protected and never shared with third parties.",
  },
  {
    question: "How much does NeuroNest cost?",
    answer:
      "NeuroNest is available as a monthly subscription. This gives you full access to all features including community places, routines, communication cards, fidget tools, check-ins, and AI-powered insights.",
  },
  {
    question: "How can I get support if something isn't working?",
    answer:
      "You can submit a support ticket directly within the app from the Settings screen. Our team will respond to your query, and you'll receive a push notification when we reply. You can also reach us at support@neuronest.global.",
  },
];

function FAQItem({ faq, index }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      data-testid={`faq-item-${index}`}
      className="border-b border-[#E7E9EC] last:border-b-0"
    >
      <button
        data-testid={`faq-toggle-${index}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-4 py-6 text-left group"
      >
        <span className="font-heading text-base md:text-lg font-semibold text-[#2F3A56] group-hover:text-brand-teal transition-colors leading-snug">
          {faq.question}
        </span>
        <ChevronDown
          size={20}
          className={`text-[#8B95A5] flex-shrink-0 mt-1 transition-transform duration-300 ${
            open ? "rotate-180 text-brand-teal" : ""
          }`}
          strokeWidth={1.5}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          open ? "max-h-[500px] pb-6" : "max-h-0"
        }`}
      >
        <p className="text-sm text-[#5A6376] leading-relaxed whitespace-pre-line">
          {faq.answer}
        </p>
      </div>
    </div>
  );
}

export default function FAQPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="faq-page" className="min-h-screen bg-[#F8F9FB]">
      <SEO
        title="Frequently Asked Questions"
        description="Everything you need to know about NeuroNest — app modes, SEND-friendly places, check-ins, routines, communication cards, fidget tools, pricing, and data security."
        path="/faq"
        jsonLd={faqSchema(FAQS)}
      />
      <Navbar />

      <main className="pt-28 pb-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-widest text-brand-teal font-semibold mb-4">
              Common questions
            </p>
            <h1
              data-testid="faq-title"
              className="font-heading text-3xl md:text-5xl font-bold text-[#2F3A56] leading-tight mb-4"
            >
              Frequently Asked Questions
            </h1>
            <p className="text-base md:text-lg text-[#5A6376] max-w-xl mx-auto leading-relaxed">
              Everything you need to know about NeuroNest. Can't find what
              you're looking for?{" "}
              <a
                href="mailto:hello@neuronest.global"
                className="text-brand-teal hover:underline"
              >
                Get in touch
              </a>
            </p>
          </div>

          {/* FAQ List */}
          <div className="bg-white rounded-3xl border border-[#E7E9EC] shadow-[0_4px_20px_rgba(0,0,0,0.04)] px-6 md:px-8">
            {FAQS.map((faq, i) => (
              <FAQItem key={i} faq={faq} index={i} />
            ))}
          </div>

          {/* Contact CTA */}
          <div className="text-center mt-12">
            <p className="text-sm text-[#5A6376] mb-2">
              Still have questions?
            </p>
            <a
              href="mailto:hello@neuronest.global"
              data-testid="faq-contact-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-brand-teal hover:underline"
            >
              Email us at hello@neuronest.global
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
